import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { 
  TrendingUp, TrendingDown, ChevronDown, ChevronRight,
  DollarSign, Target, Wallet, Calculator, BarChart3, AlertTriangle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { FinancePageLayout } from '@/components/finance';
import { useDreRealizado, type DreData, type DreSection, type DreLineItem } from '@/hooks/finance/useDreRealizado';
import { useDreProjetado, useForecastScenariosWithMultipliers } from '@/hooks/finance/useDreProjetado';
import { DreLineDetailPopover } from '@/components/finance/dre/DreLineDetailPopover';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, ComposedChart, Line
} from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

type PeriodType = 'ytd' | '12m' | '6m' | '3m';
type HorizonType = '3' | '6' | '12' | '24';

const formatCurrency = (value: number, currency: string = 'USD') => {
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value: number) => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const formatMonth = (monthStr: string) => {
  const [year, month] = monthStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return format(date, 'MMM/yy', { locale: ptBR });
};

// KPI Cards Component
function KpiCards({ data, currency, isConsolidated }: { data: DreData; currency: string; isConsolidated: boolean }) {
  const { grandTotals, grandTotalsByBu, revenueRatios } = data;
  
  return (
    <div className="space-y-4">
      {isConsolidated && revenueRatios && (
        <div className="flex gap-2 items-center text-sm text-muted-foreground">
          <span>Rateio G&A:</span>
          <Badge variant="outline">🇺🇸 USA {(revenueRatios.us * 100).toFixed(1)}%</Badge>
          <Badge variant="outline">🇧🇷 Brasil {(revenueRatios.br * 100).toFixed(1)}%</Badge>
        </div>
      )}
      
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(grandTotals.revenue, currency)}
            </div>
            {isConsolidated && grandTotalsByBu && (
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                <div>🇺🇸 {formatCurrency(grandTotalsByBu.us.revenue, 'USD')}</div>
                <div>🇧🇷 {formatCurrency(grandTotalsByBu.br.revenue, 'BRL')}</div>
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lucro Bruto</CardTitle>
            <Target className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(grandTotals.grossProfit, currency)}
            </div>
            <p className="text-xs text-muted-foreground">
              Margem: {grandTotals.grossMargin.toFixed(1)}%
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">EBITDA</CardTitle>
            <BarChart3 className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(grandTotals.ebitda, currency)}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lucro Líquido</CardTitle>
            <Wallet className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className={cn(
              "text-2xl font-bold",
              grandTotals.netProfit >= 0 ? "text-emerald-600" : "text-red-600"
            )}>
              {formatCurrency(grandTotals.netProfit, currency)}
            </div>
          </CardContent>
        </Card>
        
        <Card className={cn(
          grandTotals.netMargin >= 0 ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
        )}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Margem Líquida</CardTitle>
            <Calculator className={cn(
              "h-4 w-4",
              grandTotals.netMargin >= 0 ? "text-green-600" : "text-red-600"
            )} />
          </CardHeader>
          <CardContent>
            <div className={cn(
              "text-2xl font-bold",
              grandTotals.netMargin >= 0 ? "text-green-600" : "text-red-600"
            )}>
              {grandTotals.netMargin.toFixed(1)}%
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Chart Component
function DreChart({ data, currency }: { data: DreData; currency: string }) {
  const chartData = useMemo(() => {
    return data.months.map(m => ({
      month: formatMonth(m),
      receita: data.summary.totalRevenue[m] || 0,
      despesas: (data.summary.totalCac[m] || 0) + 
                (data.summary.totalFulfillment[m] || 0) + 
                (data.summary.totalGa[m] || 0) + 
                (data.summary.totalFinancial[m] || 0),
      lucro: data.summary.netProfit[m] || 0,
      margem: data.summary.netMargin[m] || 0,
    }));
  }, [data]);
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Evolução Financeira</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="month" className="text-xs" />
            <YAxis 
              yAxisId="left"
              tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
              className="text-xs"
            />
            <YAxis 
              yAxisId="right" 
              orientation="right"
              tickFormatter={(v) => `${v.toFixed(0)}%`}
              className="text-xs"
            />
            <Tooltip 
              formatter={(value: number, name: string) => {
                if (name === 'margem') return [`${value.toFixed(1)}%`, 'Margem'];
                return [formatCurrency(value, currency), name];
              }}
            />
            <Legend />
            <Bar yAxisId="left" dataKey="receita" name="Receita" fill="hsl(var(--chart-1))" />
            <Bar yAxisId="left" dataKey="despesas" name="Despesas" fill="hsl(var(--chart-2))" />
            <Line yAxisId="right" type="monotone" dataKey="margem" name="Margem %" stroke="hsl(var(--chart-3))" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// Consolidated DRE Table - Shows USA | Brasil | Total columns
function ConsolidatedDreTable({ data }: { data: DreData }) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  
  const toggleSection = (type: string) => {
    setExpandedSections(prev => ({ ...prev, [type]: !prev[type] }));
  };
  
  const { sections, grandTotals, grandTotalsByBu, summaryByBu, summary } = data;
  
  if (!grandTotalsByBu || !summaryByBu) return null;
  
  // Helper to calculate % of gross revenue
  const pctOfRevenue = (value: number, revenue: number) => {
    if (!revenue || revenue === 0) return '0.0%';
    return `${((value / revenue) * 100).toFixed(1)}%`;
  };
  
  const renderSectionRow = (section: DreSection, idx: number) => {
    const isRevenue = section.type === 'revenue';
    const colorClass = isRevenue ? 'text-green-600' : 'text-orange-600';
    const isExpanded = expandedSections[section.type] || false;
    
    return (
      <>
        <TableRow 
          key={section.type}
          className="cursor-pointer hover:bg-muted/50 font-medium bg-muted/30"
          onClick={() => toggleSection(section.type)}
        >
          <TableCell className="flex items-center gap-2">
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span>{section.icon}</span>
            <span>{section.name}</span>
            <Badge variant="outline" className="ml-2">{section.items.length}</Badge>
          </TableCell>
          <TableCell className={cn("text-right", colorClass)}>
            {isRevenue ? '' : '(-)'} {formatCurrency(section.totalByBu?.us || 0, 'USD')}
          </TableCell>
          <TableCell className="text-right text-muted-foreground text-xs">
            {pctOfRevenue(section.totalByBu?.us || 0, grandTotalsByBu.us.revenue)}
          </TableCell>
          <TableCell className={cn("text-right", colorClass)}>
            {isRevenue ? '' : '(-)'} {formatCurrency(section.totalByBu?.br || 0, 'BRL')}
          </TableCell>
          <TableCell className="text-right text-muted-foreground text-xs">
            {pctOfRevenue(section.totalByBu?.br || 0, grandTotalsByBu.br.revenue)}
          </TableCell>
          <TableCell className={cn("text-right font-bold", colorClass)}>
            {formatCurrency(section.total, 'USD')}
          </TableCell>
          <TableCell className="text-right text-muted-foreground text-xs font-medium">
            {pctOfRevenue(section.total, grandTotals.revenue)}
          </TableCell>
        </TableRow>
        {isExpanded && section.items.map(item => (
          <TableRow key={item.accountCode} className="text-sm">
            <TableCell className="pl-10 text-muted-foreground">
              {item.accountCode} - {item.accountName}
            </TableCell>
            <TableCell className="text-right text-muted-foreground">
              {formatCurrency(item.totalByBu?.us || 0, 'USD')}
            </TableCell>
            <TableCell className="text-right text-muted-foreground/60 text-xs">
              {pctOfRevenue(item.totalByBu?.us || 0, grandTotalsByBu.us.revenue)}
            </TableCell>
            <TableCell className="text-right text-muted-foreground">
              {formatCurrency(item.totalByBu?.br || 0, 'BRL')}
            </TableCell>
            <TableCell className="text-right text-muted-foreground/60 text-xs">
              {pctOfRevenue(item.totalByBu?.br || 0, grandTotalsByBu.br.revenue)}
            </TableCell>
            <TableCell className="text-right">
              {formatCurrency(item.total, 'USD')}
            </TableCell>
            <TableCell className="text-right text-muted-foreground/60 text-xs">
              {pctOfRevenue(item.total, grandTotals.revenue)}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Demonstrativo de Resultado
          <Badge variant="secondary">Consolidado com Rateio G&A</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">Conta</TableHead>
              <TableHead className="text-right min-w-[100px]">🇺🇸 USA (USD)</TableHead>
              <TableHead className="text-right min-w-[60px] text-xs">%</TableHead>
              <TableHead className="text-right min-w-[100px]">🇧🇷 Brasil (BRL)</TableHead>
              <TableHead className="text-right min-w-[60px] text-xs">%</TableHead>
              <TableHead className="text-right font-bold min-w-[100px]">Total (USD)</TableHead>
              <TableHead className="text-right min-w-[60px] text-xs font-medium">% Receita</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* 1. Revenue */}
            {renderSectionRow(sections[0], 0)}
            
            {/* 2. (-) CSP - Custo dos Serviços */}
            {renderSectionRow(sections[1], 1)}
            
            {/* 3. = LUCRO BRUTO */}
            <TableRow className="bg-green-50 font-bold border-t-2">
              <TableCell>= LUCRO BRUTO</TableCell>
              <TableCell className="text-right text-green-600">
                {formatCurrency(grandTotalsByBu.us.grossProfit, 'USD')}
              </TableCell>
              <TableCell className="text-right text-green-600 text-xs">
                {pctOfRevenue(grandTotalsByBu.us.grossProfit, grandTotalsByBu.us.revenue)}
              </TableCell>
              <TableCell className="text-right text-green-600">
                {formatCurrency(grandTotalsByBu.br.grossProfit, 'BRL')}
              </TableCell>
              <TableCell className="text-right text-green-600 text-xs">
                {pctOfRevenue(grandTotalsByBu.br.grossProfit, grandTotalsByBu.br.revenue)}
              </TableCell>
              <TableCell className="text-right text-green-600">
                {formatCurrency(grandTotals.grossProfit, 'USD')}
              </TableCell>
              <TableCell className="text-right text-green-600 text-xs font-medium">
                {pctOfRevenue(grandTotals.grossProfit, grandTotals.revenue)}
              </TableCell>
            </TableRow>
            
            {/* 4. (-) CAC */}
            {renderSectionRow(sections[2], 2)}
            
            {/* 5. = MARGEM DE CONTRIBUIÇÃO */}
            <TableRow className="bg-purple-50 font-bold border-t-2">
              <TableCell>= MARGEM DE CONTRIBUIÇÃO</TableCell>
              <TableCell className="text-right text-purple-600">
                {formatCurrency(grandTotalsByBu.us.marginContribution, 'USD')}
              </TableCell>
              <TableCell className="text-right text-purple-600 text-xs">
                {pctOfRevenue(grandTotalsByBu.us.marginContribution, grandTotalsByBu.us.revenue)}
              </TableCell>
              <TableCell className="text-right text-purple-600">
                {formatCurrency(grandTotalsByBu.br.marginContribution, 'BRL')}
              </TableCell>
              <TableCell className="text-right text-purple-600 text-xs">
                {pctOfRevenue(grandTotalsByBu.br.marginContribution, grandTotalsByBu.br.revenue)}
              </TableCell>
              <TableCell className="text-right text-purple-600">
                {formatCurrency(grandTotals.marginContribution, 'USD')}
              </TableCell>
              <TableCell className="text-right text-purple-600 text-xs font-medium">
                {pctOfRevenue(grandTotals.marginContribution, grandTotals.revenue)}
              </TableCell>
            </TableRow>
            
            {/* 6. (-) G&A */}
            {renderSectionRow(sections[3], 3)}
            
            {/* 7. = EBITDA */}
            <TableRow className="bg-amber-50 font-bold border-t-2">
              <TableCell>= EBITDA</TableCell>
              <TableCell className="text-right text-amber-600">
                {formatCurrency(grandTotalsByBu.us.ebitda, 'USD')}
              </TableCell>
              <TableCell className="text-right text-amber-600 text-xs">
                {pctOfRevenue(grandTotalsByBu.us.ebitda, grandTotalsByBu.us.revenue)}
              </TableCell>
              <TableCell className="text-right text-amber-600">
                {formatCurrency(grandTotalsByBu.br.ebitda, 'BRL')}
              </TableCell>
              <TableCell className="text-right text-amber-600 text-xs">
                {pctOfRevenue(grandTotalsByBu.br.ebitda, grandTotalsByBu.br.revenue)}
              </TableCell>
              <TableCell className="text-right text-amber-600">
                {formatCurrency(grandTotals.ebitda, 'USD')}
              </TableCell>
              <TableCell className="text-right text-amber-600 text-xs font-medium">
                {pctOfRevenue(grandTotals.ebitda, grandTotals.revenue)}
              </TableCell>
            </TableRow>
            
            {/* 8. (-) Financial */}
            {renderSectionRow(sections[4], 4)}
            
            {/* 9. = LUCRO LÍQUIDO */}
            <TableRow className={cn(
              "font-bold text-lg border-t-4",
              grandTotals.netProfit >= 0 ? "bg-green-50" : "bg-red-50"
            )}>
              <TableCell>= LUCRO LÍQUIDO</TableCell>
              <TableCell className={cn(
                "text-right",
                grandTotalsByBu.us.netProfit >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {formatCurrency(grandTotalsByBu.us.netProfit, 'USD')}
              </TableCell>
              <TableCell className={cn(
                "text-right text-xs",
                grandTotalsByBu.us.netProfit >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {pctOfRevenue(grandTotalsByBu.us.netProfit, grandTotalsByBu.us.revenue)}
              </TableCell>
              <TableCell className={cn(
                "text-right",
                grandTotalsByBu.br.netProfit >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {formatCurrency(grandTotalsByBu.br.netProfit, 'BRL')}
              </TableCell>
              <TableCell className={cn(
                "text-right text-xs",
                grandTotalsByBu.br.netProfit >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {pctOfRevenue(grandTotalsByBu.br.netProfit, grandTotalsByBu.br.revenue)}
              </TableCell>
              <TableCell className={cn(
                "text-right",
                grandTotals.netProfit >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {formatCurrency(grandTotals.netProfit, 'USD')}
              </TableCell>
              <TableCell className={cn(
                "text-right text-xs font-medium",
                grandTotals.netProfit >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {pctOfRevenue(grandTotals.netProfit, grandTotals.revenue)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// Single BU DRE Table - Shows months columns with clickable details
function SingleBuDreTable({ data, currency, showDetails = false }: { data: DreData; currency: string; showDetails?: boolean }) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  
  const toggleSection = (type: string) => {
    setExpandedSections(prev => ({ ...prev, [type]: !prev[type] }));
  };
  
  const visibleMonths = data.months.slice(-6);
  
  // Get gross revenue for each month from sections[0] (revenue section)
  const getGrossRevenueForMonth = (month: string) => {
    const revenueSection = data.sections[0];
    return revenueSection?.subtotal?.[month] || 0;
  };
  
  // Helper to calculate % of gross revenue for a specific month
  const pctOfRevenueMonth = (value: number, month: string) => {
    const grossRevenue = getGrossRevenueForMonth(month);
    if (!grossRevenue || grossRevenue === 0) return '0.0%';
    return `${((value / grossRevenue) * 100).toFixed(1)}%`;
  };
  
  // Helper for total % (uses grand total revenue)
  const pctOfRevenueTotal = (value: number) => {
    const grossRevenue = data.grandTotals.revenue;
    if (!grossRevenue || grossRevenue === 0) return '0.0%';
    return `${((value / grossRevenue) * 100).toFixed(1)}%`;
  };
  
  const renderItemCell = (item: DreLineItem, month: string, monthLabel: string) => {
    const amount = item.amounts[month] || 0;
    const details = item.sourceDetails?.[month];
    
    if (showDetails && details && details.length > 0) {
      return (
        <DreLineDetailPopover
          accountCode={item.accountCode}
          accountName={item.accountName}
          month={month}
          monthLabel={monthLabel}
          amount={amount}
          sourceDetails={details}
          currency={currency}
        >
          <div className="flex flex-col">
            <span>{formatCurrency(amount, currency)}</span>
            <span className="text-[10px] text-muted-foreground">{pctOfRevenueMonth(amount, month)}</span>
          </div>
        </DreLineDetailPopover>
      );
    }
    
    return (
      <div className="flex flex-col">
        <span>{formatCurrency(amount, currency)}</span>
        <span className="text-[10px] text-muted-foreground">{pctOfRevenueMonth(amount, month)}</span>
      </div>
    );
  };
  
  const renderSectionRow = (section: DreSection) => {
    const isRevenue = section.type === 'revenue';
    const colorClass = isRevenue ? 'text-green-600' : 'text-orange-600';
    const isExpanded = expandedSections[section.type] || false;
    
    return (
      <>
        <TableRow 
          key={section.type}
          className="cursor-pointer hover:bg-muted/50 font-medium bg-muted/30"
          onClick={() => toggleSection(section.type)}
        >
          <TableCell className="flex items-center gap-2">
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span>{section.icon}</span>
            <span>{section.name}</span>
            <Badge variant="outline" className="ml-2">{section.items.length}</Badge>
          </TableCell>
          {visibleMonths.map(m => (
            <TableCell key={m} className={cn("text-right", colorClass)}>
              <div className="flex flex-col">
                <span>{isRevenue ? '' : '(-)'} {formatCurrency(section.subtotal[m] || 0, currency)}</span>
                <span className="text-[10px] text-muted-foreground">{pctOfRevenueMonth(section.subtotal[m] || 0, m)}</span>
              </div>
            </TableCell>
          ))}
          <TableCell className={cn("text-right font-bold", colorClass)}>
            <div className="flex flex-col">
              <span>{formatCurrency(section.total, currency)}</span>
              <span className="text-[10px] text-muted-foreground">{pctOfRevenueTotal(section.total)}</span>
            </div>
          </TableCell>
        </TableRow>
        {isExpanded && section.items.map(item => (
          <TableRow key={item.accountCode} className="text-sm">
            <TableCell className="pl-10 text-muted-foreground">
              {item.accountCode} - {item.accountName}
            </TableCell>
            {visibleMonths.map(m => (
              <TableCell key={m} className="text-right text-muted-foreground">
                {renderItemCell(item, m, formatMonth(m))}
              </TableCell>
            ))}
            <TableCell className="text-right">
              <div className="flex flex-col">
                <span>{formatCurrency(item.total, currency)}</span>
                <span className="text-[10px] text-muted-foreground/60">{pctOfRevenueTotal(item.total)}</span>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Demonstrativo de Resultado</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">Conta</TableHead>
              {visibleMonths.map(m => (
                <TableHead key={m} className="text-right min-w-[100px]">
                  <div className="flex flex-col">
                    <span>{formatMonth(m)}</span>
                    <span className="text-[10px] text-muted-foreground font-normal">valor / %</span>
                  </div>
                </TableHead>
              ))}
              <TableHead className="text-right font-bold min-w-[100px]">
                <div className="flex flex-col">
                  <span>Total</span>
                  <span className="text-[10px] text-muted-foreground font-normal">valor / %</span>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* 1. Receita Bruta */}
            {renderSectionRow(data.sections[0])}
            {/* 2. (-) Deduções da Receita (PDD + Cancelamentos) */}
            {data.sections[1] && renderSectionRow(data.sections[1])}
            
            {/* 3. = RECEITA LÍQUIDA */}
            <TableRow className="bg-blue-50 font-bold border-t-2">
              <TableCell>= RECEITA LÍQUIDA</TableCell>
              {visibleMonths.map(m => {
                const value = data.summary.totalRevenue[m] || 0;
                return (
                  <TableCell key={m} className="text-right text-blue-600">
                    <div className="flex flex-col">
                      <span>{formatCurrency(value, currency)}</span>
                      <span className="text-[10px] text-blue-400">{pctOfRevenueMonth(value, m)}</span>
                    </div>
                  </TableCell>
                );
              })}
              <TableCell className="text-right text-blue-600">
                <div className="flex flex-col">
                  <span>{formatCurrency(
                    Object.values(data.summary.totalRevenue || {}).reduce((a, b) => a + (b || 0), 0), 
                    currency
                  )}</span>
                  <span className="text-[10px] text-blue-400">
                    {pctOfRevenueTotal(Object.values(data.summary.totalRevenue || {}).reduce((a, b) => a + (b || 0), 0))}
                  </span>
                </div>
              </TableCell>
            </TableRow>
            
            {/* 4. (-) CSP - Custo dos Serviços Prestados */}
            {data.sections[2] && renderSectionRow(data.sections[2])}
            
            {/* 5. = LUCRO BRUTO */}
            <TableRow className="bg-green-50 font-bold border-t-2">
              <TableCell>= LUCRO BRUTO</TableCell>
              {visibleMonths.map(m => {
                const value = data.summary.grossProfit[m] || 0;
                return (
                  <TableCell key={m} className="text-right text-green-600">
                    <div className="flex flex-col">
                      <span>{formatCurrency(value, currency)}</span>
                      <span className="text-[10px] text-green-400">{pctOfRevenueMonth(value, m)}</span>
                    </div>
                  </TableCell>
                );
              })}
              <TableCell className="text-right text-green-600">
                <div className="flex flex-col">
                  <span>{formatCurrency(data.grandTotals.grossProfit, currency)}</span>
                  <span className="text-[10px] text-green-400">{pctOfRevenueTotal(data.grandTotals.grossProfit)}</span>
                </div>
              </TableCell>
            </TableRow>
            
            {/* 6. (-) CAC - Custo de Aquisição */}
            {data.sections[3] && renderSectionRow(data.sections[3])}
            
            {/* 7. = MARGEM DE CONTRIBUIÇÃO */}
            <TableRow className="bg-purple-50 font-bold border-t-2">
              <TableCell>= MARGEM DE CONTRIBUIÇÃO</TableCell>
              {visibleMonths.map(m => {
                const value = data.summary.marginContribution[m] || 0;
                return (
                  <TableCell key={m} className="text-right text-purple-600">
                    <div className="flex flex-col">
                      <span>{formatCurrency(value, currency)}</span>
                      <span className="text-[10px] text-purple-400">{pctOfRevenueMonth(value, m)}</span>
                    </div>
                  </TableCell>
                );
              })}
              <TableCell className="text-right text-purple-600">
                <div className="flex flex-col">
                  <span>{formatCurrency(data.grandTotals.marginContribution || 0, currency)}</span>
                  <span className="text-[10px] text-purple-400">{pctOfRevenueTotal(data.grandTotals.marginContribution || 0)}</span>
                </div>
              </TableCell>
            </TableRow>
            
            {/* 8. (-) G&A - Despesas Administrativas */}
            {data.sections[4] && renderSectionRow(data.sections[4])}
            
            {/* 9. = EBITDA */}
            <TableRow className="bg-amber-50 font-bold border-t-2">
              <TableCell>= EBITDA</TableCell>
              {visibleMonths.map(m => {
                const value = data.summary.ebitda[m] || 0;
                return (
                  <TableCell key={m} className="text-right text-amber-600">
                    <div className="flex flex-col">
                      <span>{formatCurrency(value, currency)}</span>
                      <span className="text-[10px] text-amber-400">{pctOfRevenueMonth(value, m)}</span>
                    </div>
                  </TableCell>
                );
              })}
              <TableCell className="text-right text-amber-600">
                <div className="flex flex-col">
                  <span>{formatCurrency(data.grandTotals.ebitda, currency)}</span>
                  <span className="text-[10px] text-amber-400">{pctOfRevenueTotal(data.grandTotals.ebitda)}</span>
                </div>
              </TableCell>
            </TableRow>
            
            {/* 10. (-) Despesas Financeiras */}
            {data.sections[5] && renderSectionRow(data.sections[5])}
            
            {/* 11. = LUCRO LÍQUIDO */}
            <TableRow className={cn(
              "font-bold text-lg border-t-4",
              data.grandTotals.netProfit >= 0 ? "bg-green-50" : "bg-red-50"
            )}>
              <TableCell>= LUCRO LÍQUIDO</TableCell>
              {visibleMonths.map(m => {
                const value = data.summary.netProfit[m] || 0;
                return (
                  <TableCell 
                    key={m} 
                    className={cn(
                      "text-right",
                      value >= 0 ? "text-green-600" : "text-red-600"
                    )}
                  >
                    <div className="flex flex-col">
                      <span>{formatCurrency(value, currency)}</span>
                      <span className={cn("text-[10px]", value >= 0 ? "text-green-400" : "text-red-400")}>
                        {pctOfRevenueMonth(value, m)}
                      </span>
                    </div>
                  </TableCell>
                );
              })}
              <TableCell className={cn(
                "text-right",
                data.grandTotals.netProfit >= 0 ? "text-green-600" : "text-red-600"
              )}>
                <div className="flex flex-col">
                  <span>{formatCurrency(data.grandTotals.netProfit, currency)}</span>
                  <span className={cn("text-[10px]", data.grandTotals.netProfit >= 0 ? "text-green-400" : "text-red-400")}>
                    {pctOfRevenueTotal(data.grandTotals.netProfit)}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// Scenario Comparison Component
function ScenarioComparison({ businessUnit }: { businessUnit: string }) {
  const { data: scenarios } = useForecastScenariosWithMultipliers();
  
  const { data: baseData } = useDreProjetado({ 
    horizonMonths: 12, 
    scenarioId: scenarios?.find(s => s.name.toLowerCase().includes('base'))?.id,
    businessUnit 
  });
  
  const { data: optimisticData } = useDreProjetado({ 
    horizonMonths: 12, 
    scenarioId: scenarios?.find(s => s.name.toLowerCase().includes('otimista'))?.id,
    businessUnit 
  });
  
  const { data: pessimisticData } = useDreProjetado({ 
    horizonMonths: 12, 
    scenarioId: scenarios?.find(s => s.name.toLowerCase().includes('pessimista'))?.id,
    businessUnit 
  });
  
  if (!baseData || !optimisticData || !pessimisticData) {
    return <Skeleton className="h-[400px] w-full" />;
  }
  
  const currency = businessUnit === 'br' ? 'BRL' : 'USD';
  
  const comparisonData = [
    {
      metric: 'Receita',
      base: baseData.grandTotals.revenue,
      otimista: optimisticData.grandTotals.revenue,
      pessimista: pessimisticData.grandTotals.revenue,
    },
    {
      metric: 'Lucro Bruto',
      base: baseData.grandTotals.grossProfit,
      otimista: optimisticData.grandTotals.grossProfit,
      pessimista: pessimisticData.grandTotals.grossProfit,
    },
    {
      metric: 'EBITDA',
      base: baseData.grandTotals.ebitda,
      otimista: optimisticData.grandTotals.ebitda,
      pessimista: pessimisticData.grandTotals.ebitda,
    },
    {
      metric: 'Lucro Líquido',
      base: baseData.grandTotals.netProfit,
      otimista: optimisticData.grandTotals.netProfit,
      pessimista: pessimisticData.grandTotals.netProfit,
    },
  ];
  
  return (
    <div className="space-y-6">
      {/* Scenario Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-yellow-200 bg-yellow-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-2xl">⚖️</span>
              Base (Realista)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Receita</span>
                <span className="font-medium">{formatCurrency(baseData.grandTotals.revenue, currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lucro Líquido</span>
                <span className={cn(
                  "font-medium",
                  baseData.grandTotals.netProfit >= 0 ? "text-green-600" : "text-red-600"
                )}>
                  {formatCurrency(baseData.grandTotals.netProfit, currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Margem</span>
                <span className={cn(
                  "font-medium",
                  baseData.grandTotals.netMargin >= 0 ? "text-green-600" : "text-red-600"
                )}>
                  {baseData.grandTotals.netMargin.toFixed(1)}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-2xl">🚀</span>
              Otimista
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Receita</span>
                <span className="font-medium">{formatCurrency(optimisticData.grandTotals.revenue, currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lucro Líquido</span>
                <span className={cn(
                  "font-medium",
                  optimisticData.grandTotals.netProfit >= 0 ? "text-green-600" : "text-red-600"
                )}>
                  {formatCurrency(optimisticData.grandTotals.netProfit, currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Margem</span>
                <span className={cn(
                  "font-medium",
                  optimisticData.grandTotals.netMargin >= 0 ? "text-green-600" : "text-red-600"
                )}>
                  {optimisticData.grandTotals.netMargin.toFixed(1)}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-red-200 bg-red-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-2xl">⚠️</span>
              Pessimista
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Receita</span>
                <span className="font-medium">{formatCurrency(pessimisticData.grandTotals.revenue, currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Lucro Líquido</span>
                <span className={cn(
                  "font-medium",
                  pessimisticData.grandTotals.netProfit >= 0 ? "text-green-600" : "text-red-600"
                )}>
                  {formatCurrency(pessimisticData.grandTotals.netProfit, currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Margem</span>
                <span className={cn(
                  "font-medium",
                  pessimisticData.grandTotals.netMargin >= 0 ? "text-green-600" : "text-red-600"
                )}>
                  {pessimisticData.grandTotals.netMargin.toFixed(1)}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Comparison Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Comparativo de Cenários</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={comparisonData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="metric" className="text-xs" />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} className="text-xs" />
              <Tooltip formatter={(value: number) => formatCurrency(value, currency)} />
              <Legend />
              <Bar dataKey="pessimista" name="Pessimista" fill="hsl(var(--destructive))" />
              <Bar dataKey="base" name="Base" fill="hsl(var(--primary))" />
              <Bar dataKey="otimista" name="Otimista" fill="hsl(var(--chart-1))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

// Deduction Sliders Component (PDD and Cancellation)
function DeductionSliders({ 
  pddRate,
  cancellationRate,
  onPddChange,
  onCancellationChange,
  grossRevenue,
  currency,
}: { 
  pddRate: number;
  cancellationRate: number;
  onPddChange: (value: number) => void;
  onCancellationChange: (value: number) => void;
  grossRevenue: number;
  currency: string;
}) {
  const [localPdd, setLocalPdd] = useState(pddRate * 100);
  const [localCancel, setLocalCancel] = useState(cancellationRate * 100);
  
  const pddAmount = grossRevenue * (localPdd / 100);
  const cancelAmount = grossRevenue * (localCancel / 100);
  const netRevenue = grossRevenue - pddAmount - cancelAmount;
  
  return (
    <Card>
      <CardContent className="pt-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Deduções da Receita Bruta</span>
            <div className="text-xs text-muted-foreground">
              Receita Bruta: {formatCurrency(grossRevenue, currency)}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* PDD Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <span>PDD (Inadimplência)</span>
                </div>
                <Badge variant={localPdd > 20 ? "destructive" : "secondary"} className="min-w-[50px] justify-center">
                  {localPdd.toFixed(0)}%
                </Badge>
              </div>
              <Slider
                value={[localPdd]}
                onValueChange={(v) => setLocalPdd(v[0])}
                onValueCommit={(v) => onPddChange(v[0] / 100)}
                min={0}
                max={100}
                step={1}
                className="cursor-pointer"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Clientes que não pagam todas as parcelas</span>
                <span className="text-amber-600 font-medium">-{formatCurrency(pddAmount, currency)}</span>
              </div>
            </div>
            
            {/* Cancellation Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <TrendingDown className="h-4 w-4 text-red-500" />
                  <span>Cancelamentos</span>
                </div>
                <Badge variant={localCancel > 15 ? "destructive" : "secondary"} className="min-w-[50px] justify-center">
                  {localCancel.toFixed(0)}%
                </Badge>
              </div>
              <Slider
                value={[localCancel]}
                onValueChange={(v) => setLocalCancel(v[0])}
                onValueCommit={(v) => onCancellationChange(v[0] / 100)}
                min={0}
                max={100}
                step={1}
                className="cursor-pointer"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Clientes que desistem</span>
                <span className="text-red-600 font-medium">-{formatCurrency(cancelAmount, currency)}</span>
              </div>
            </div>
          </div>
          
          {/* Net Revenue */}
          <div className="pt-3 border-t flex items-center justify-between">
            <span className="font-medium">Receita Líquida</span>
            <span className="text-lg font-bold text-green-600">
              {formatCurrency(netRevenue, currency)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function Dre() {
  const [activeTab, setActiveTab] = useState('realizado');
  const [businessUnit, setBusinessUnit] = useState<string>('all');
  const [period, setPeriod] = useState<PeriodType>('12m');
  const [horizon, setHorizon] = useState<HorizonType>('12');
  const [selectedScenario, setSelectedScenario] = useState<string>('');
  const [pddRate, setPddRate] = useState<number>(0.30); // Default 30%
  const [cancellationRate, setCancellationRate] = useState<number>(0.10); // Default 10%
  
  const queryClient = useQueryClient();
  const { data: scenarios } = useForecastScenariosWithMultipliers();
  
  const { data: dreRealizado, isLoading: isLoadingRealizado } = useDreRealizado({
    period,
    businessUnit,
  });
  
  const { data: dreProjetado, isLoading: isLoadingProjetado } = useDreProjetado({
    horizonMonths: parseInt(horizon),
    scenarioId: selectedScenario || undefined,
    businessUnit,
    pddRate,
    cancellationRate,
  });
  
  const isConsolidated = !businessUnit || businessUnit === 'all';
  const currency = businessUnit === 'br' ? 'BRL' : 'USD';
  
  return (
    <FinancePageLayout>
      
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">DRE - Demonstrativo de Resultado</h1>
        
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={businessUnit} onValueChange={setBusinessUnit}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Business Unit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Consolidado</SelectItem>
              <SelectItem value="usa">🇺🇸 USA</SelectItem>
              <SelectItem value="br">🇧🇷 Brasil</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="realizado">Realizado</TabsTrigger>
          <TabsTrigger value="projetado">Projetado</TabsTrigger>
          <TabsTrigger value="comparativo">Comparativo</TabsTrigger>
        </TabsList>
        
        <TabsContent value="realizado" className="space-y-6 mt-6">
          <div className="flex gap-2">
            <Select value={period} onValueChange={(v) => setPeriod(v as PeriodType)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ytd">YTD</SelectItem>
                <SelectItem value="12m">Últimos 12m</SelectItem>
                <SelectItem value="6m">Últimos 6m</SelectItem>
                <SelectItem value="3m">Últimos 3m</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {isLoadingRealizado ? (
            <div className="space-y-4">
              <Skeleton className="h-[100px] w-full" />
              <Skeleton className="h-[300px] w-full" />
              <Skeleton className="h-[500px] w-full" />
            </div>
          ) : dreRealizado ? (
            <>
              <KpiCards data={dreRealizado} currency={currency} isConsolidated={isConsolidated} />
              <DreChart data={dreRealizado} currency={currency} />
              {isConsolidated ? (
                <ConsolidatedDreTable data={dreRealizado} />
              ) : (
                <SingleBuDreTable data={dreRealizado} currency={currency} />
              )}
            </>
          ) : (
            <Card>
              <CardContent className="py-10">
                <p className="text-center text-muted-foreground">Nenhum dado disponível</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="projetado" className="space-y-6 mt-6">
          <div className="flex gap-2">
            <Select value={horizon} onValueChange={(v) => setHorizon(v as HorizonType)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Horizonte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 meses</SelectItem>
                <SelectItem value="6">6 meses</SelectItem>
                <SelectItem value="12">12 meses</SelectItem>
                <SelectItem value="24">24 meses</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedScenario || 'default'} onValueChange={(v) => setSelectedScenario(v === 'default' ? '' : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Cenário" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Padrão</SelectItem>
                {scenarios?.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name} {s.is_default && '(Padrão)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <DeductionSliders 
            pddRate={pddRate}
            cancellationRate={cancellationRate}
            onPddChange={setPddRate}
            onCancellationChange={setCancellationRate}
            grossRevenue={dreProjetado?.grandTotals?.revenue || 0}
            currency={currency}
          />
          
          {isLoadingProjetado ? (
            <div className="space-y-4">
              <Skeleton className="h-[100px] w-full" />
              <Skeleton className="h-[300px] w-full" />
              <Skeleton className="h-[500px] w-full" />
            </div>
          ) : dreProjetado ? (
            <>
              <KpiCards data={dreProjetado} currency={currency} isConsolidated={false} />
              <DreChart data={dreProjetado} currency={currency} />
              <SingleBuDreTable data={dreProjetado} currency={currency} showDetails={true} />
            </>
          ) : (
            <Card>
              <CardContent className="py-10">
                <p className="text-center text-muted-foreground">Nenhum dado disponível</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="comparativo" className="space-y-6 mt-6">
          <ScenarioComparison businessUnit={businessUnit} />
        </TabsContent>
      </Tabs>
    </FinancePageLayout>
  );
}
